import { AxiosError } from 'axios';
import { api } from '@shared/api';
import {
  parseOnboardingStarter,
  parseStartedModule,
  type OnboardingStarter,
  type ReminderFields,
  type StartedModule,
} from '../model/flow';

/** Стартовый модуль и «другие темы» (официальные модули Lomora без IT). */
export async function fetchOnboardingStarter(): Promise<OnboardingStarter> {
  const { data } = await api.get<unknown>('/onboarding/starter');
  return parseOnboardingStarter(data);
}

export class OnboardingResponseError extends Error {
  constructor() {
    super('ONBOARDING_BAD_RESPONSE');
    this.name = 'OnboardingResponseError';
  }
}

/**
 * Добавить копию стартового модуля в аккаунт — без подтверждения почты. Без `sourceId` сервер берёт
 * закреплённый модуль («Пропустить»). Повтор безопасен: сервер вернёт уже добавленную копию.
 * Тело — `{}`, а не пустое: FastAPI требует тело, даже если все поля необязательны.
 */
export async function startOnboarding(sourceId?: string): Promise<StartedModule> {
  const { data } = await api.post<unknown>('/onboarding/start', sourceId ? { source_id: sourceId } : {});
  const started = parseStartedModule(data);
  if (!started) throw new OnboardingResponseError();
  return started;
}

/**
 * Тело `POST /profile/finish-onboarding` (snake_case явно — запросы интерцептор не переводит).
 * `module_ids: []` — стартовый модуль уже добавил `/onboarding/start`; поле оставлено, потому что
 * схема бэкенда с `extra="forbid"` и прежде требовала его.
 */
export interface FinishOnboardingPayload extends ReminderFields {
  module_ids: string[];
}

function errorCode(err: AxiosError): string | undefined {
  return (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
}

/** `finished` — знакомство завершено этим запросом (поля напоминаний применены); `already` — было
 *  завершено раньше, поля этого запроса сервер не применил. */
export type FinishOutcome = 'finished' | 'already';

/** 409 ALREADY_FINISHED_ONBOARDING — знакомство уже завершено (повторное нажатие, вторая вкладка, вход
 *  на итог, B5): это успех, но выбор времени из этого запроса тогда не сохранён — см. FinishOutcome. */
export async function finishOnboarding(payload: FinishOnboardingPayload): Promise<FinishOutcome> {
  try {
    await api.post('/profile/finish-onboarding', payload);
    return 'finished';
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 409 && errorCode(err) === 'ALREADY_FINISHED_ONBOARDING') {
      return 'already';
    }
    throw err;
  }
}

export type OnboardingErrorKind = 'network' | 'rate_limit' | 'other';

/** Что сказать ученику: нет связи, слишком часто — или общее «попробуй ещё раз». */
export function onboardingErrorKind(err: unknown): OnboardingErrorKind {
  if (!(err instanceof AxiosError)) return 'other';
  if (!err.response) return 'network';
  if (err.response.status === 429) return 'rate_limit';
  return 'other';
}
